import { useState, useEffect, useCallback } from 'react';
import { getCompletedSessions, deleteCartSession, clearCompletedSessions } from '../lib/storage';
import { getEditionFullName } from '../lib/utils';
import type { CartSession } from '../lib/types';

export interface UseSessionHistoryReturn {
  sessions: CartSession[];
  isLoading: boolean;
  deleteSession: (id: string) => void;
  clearAll: () => void;
  copyToClipboard: (session: CartSession) => Promise<boolean>;
  refresh: () => void;
}

export function useSessionHistory(): UseSessionHistoryReturn {
  const [sessions, setSessions] = useState<CartSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const completed = await getCompletedSessions();
      setSessions(completed);
    } catch (err) {
      console.error('Failed to load session history:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteCartSession(id);
        setSessions((prev) => prev.filter((s) => s.id !== id));
      } catch (err) {
        console.error('Failed to delete session:', err);
      }
    },
    []
  );

  const handleClearAll = useCallback(async () => {
    try {
      await clearCompletedSessions();
      setSessions([]);
    } catch (err) {
      console.error('Failed to clear sessions:', err);
    }
  }, []);

  const copyToClipboard = useCallback(
    async (session: CartSession): Promise<boolean> => {
      const modeLabel = session.mode === 'buy' ? 'Buy' : 'Sell';
      const date = new Date(session.completedAt || session.createdAt).toLocaleString(
        'en-US',
        {
          month: 'numeric',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }
      );

      const lines: string[] = [];
      lines.push(`${modeLabel} Session \u2014 ${date}`);
      if (session.label) {
        lines.push(`Customer: ${session.label}`);
      }
      lines.push('================================');

      for (const item of session.items) {
        const condLabel = item.condition.toUpperCase();
        const editionStr = item.edition ? ` [${getEditionFullName(item.edition)}]` : '';
        const priceStr = `$${item.finalPrice.toFixed(2)}`;
        const wasStr =
          item.finalPrice !== item.originalPrice
            ? ` (was $${item.originalPrice.toFixed(2)})`
            : '';
        lines.push(
          `${item.cardName} (${item.setName} #${item.cardNumber}) \u2014 ${condLabel}${editionStr} \u2014 ${priceStr}${wasStr}`
        );
      }

      const total = session.items.reduce((sum, i) => sum + i.finalPrice, 0);
      lines.push('================================');
      lines.push(
        `Total: $${total.toFixed(2)} (${session.items.length} card${session.items.length !== 1 ? 's' : ''})`
      );

      try {
        await navigator.clipboard.writeText(lines.join('\n'));
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  return {
    sessions,
    isLoading,
    deleteSession: handleDelete,
    clearAll: handleClearAll,
    copyToClipboard,
    refresh: load,
  };
}
